import { WIDGET_TYPES, CHART_TYPES } from '@/utils/consts';
import { listIncludes, isValueExist } from '@/utils/utils';

export const checkValidFieldByWidgetType = (widgetType, graphType) =>
    R.allPass([
        checkShowInWidgetByType(widgetType, graphType),
        R.complement(checkHideInWidgetByType(widgetType, graphType)),
    ]);

const checkShowInWidgetByType = (widgetType, graphType) =>
    R.ifElse(
        R.has('showInWidget'),
        R.propSatisfies(
            R.compose(
                R.anyPass([R.includes(widgetType), R.includes(graphType)]),
                R.defaultTo([])
            ),
            'showInWidget'
        ),
        R.T
    );

const checkHideInWidgetByType = (widgetType, graphType) =>
    R.ifElse(
        R.has('hideInWidget'),
        R.propSatisfies(
            R.compose(
                R.anyPass([R.includes(widgetType), R.includes(graphType)]),
                R.defaultTo([])
            ),
            'hideInWidget'
        ),
        R.F
    );

export const checkValidFieldByFeatureType = (type) =>
    R.allPass([
        checkShowInFeatureByType(type),
        R.complement(checkHideInFeatureByType(type)),
    ]);

const checkShowInFeatureByType = (type) =>
    R.ifElse(
        R.has('showInFeature'),
        R.propSatisfies(
            R.compose(R.includes(type), R.defaultTo([])),
            'showInFeature'
        ),
        R.T
    );

const checkHideInFeatureByType = (type) =>
    R.ifElse(
        R.has('hideInFeature'),
        R.propSatisfies(
            R.compose(R.includes(type), R.defaultTo([])),
            'hideInFeature'
        ),
        R.F
    );

const injectCustomBreakdownInfo = (dimension) => {
    const id = R.prop('id', dimension);
    const labels = R.prop('labels', dimension);
    const name = R.prop('name', dimension);

    if (!isValueExist(labels)) {
        return dimension;
    }

    return R.assoc(
        'labels',
        R.map(
            R.mergeLeft({ customBreakdownId: id, customBreakdownName: name }),
            labels
        ),
        dimension
    );
};

export const refineDimensionToLabel = R.compose(
    R.flatten,
    R.map(R.prop('labels')),
    R.map(injectCustomBreakdownInfo),
    R.filter(R.pathEq(['config', 'type'], 'manual'))
);

export const FILTER_WIDGET_AVAILABLE_LABEL_TYPES = {
    LABEL: 'label',
    BREAKDOWN: 'breakdown',
};

export const normalizeFilterWidgetAvailableLabel = (label = {}) => {
    if (label?.type === FILTER_WIDGET_AVAILABLE_LABEL_TYPES.BREAKDOWN) {
        return {
            type: FILTER_WIDGET_AVAILABLE_LABEL_TYPES.BREAKDOWN,
            id: label.id,
        };
    }

    return {
        type: FILTER_WIDGET_AVAILABLE_LABEL_TYPES.LABEL,
        id: label.id,
        customBreakdownId: label?.customBreakdownId,
    };
};

export const getFilterWidgetAvailableLabelKey = (label = {}) => {
    const normalizedLabel = normalizeFilterWidgetAvailableLabel(label);
    return `${normalizedLabel.type}:${normalizedLabel.id}`;
};

export const isFilterWidgetBreakdownLabel = (label = {}) =>
    normalizeFilterWidgetAvailableLabel(label).type ===
    FILTER_WIDGET_AVAILABLE_LABEL_TYPES.BREAKDOWN;

const getStringSet = (values = []) =>
    new Set(
        (values || [])
            .filter((value) => !R.isNil(value) && String(value) !== '')
            .map(String)
    );

const getScopedAccountSets = (groupId, tableAdAccountsByGroupId = {}) => {
    const scopedAccounts = R.propOr([], groupId, tableAdAccountsByGroupId);

    return {
        accountIds: getStringSet(
            scopedAccounts.flatMap((account = {}) => [
                account.id,
                account.adAccountId,
                account.accountId,
                account.channelId,
            ])
        ),
        channels: getStringSet(
            scopedAccounts.flatMap((account = {}) => [
                account.channel,
                account.channelId,
                account.groupId,
                account.dataSourceName,
                account.displayChannelName,
                account.displayChannelNameKR,
            ])
        ),
    };
};

const hasIntersection = (values = [], valueSet = new Set()) =>
    (values || [])
        .filter((value) => !R.isNil(value) && String(value) !== '')
        .some((value) => valueSet.has(String(value)));

const checkFilterIdsAgainstGroup = (ids = [], scopedSets) => {
    const supportedIds = (ids || []).filter((entry) => {
        const level = String(entry?.level || '').toLowerCase();
        return (
            isValueExist(entry?.value) &&
            (level.includes('account') || level.includes('channel'))
        );
    });

    if (!supportedIds.length) {
        return null;
    }

    const accountIds = supportedIds
        .filter((entry) =>
            String(entry?.level || '')
                .toLowerCase()
                .includes('account')
        )
        .map((entry) => entry?.value);
    const channelIds = supportedIds
        .filter((entry) =>
            String(entry?.level || '')
                .toLowerCase()
                .includes('channel')
        )
        .map((entry) => entry?.value);

    return (
        (!accountIds.length ||
            hasIntersection(accountIds, scopedSets.accountIds)) &&
        (!channelIds.length || hasIntersection(channelIds, scopedSets.channels))
    );
};

export const checkDimensionMatchesGroup = (
    dimension = {},
    groupId,
    tableAdAccountsByGroupId = {}
) => {
    if (!groupId) {
        return true;
    }

    const dashboardFilter = dimension?.dashboardFilter || {};
    const scopedSets = getScopedAccountSets(groupId, tableAdAccountsByGroupId);
    const checks = [];

    if (Array.isArray(dashboardFilter.adAccountIds)) {
        checks.push(
            hasIntersection(dashboardFilter.adAccountIds, scopedSets.accountIds)
        );
    }

    if (Array.isArray(dashboardFilter.channels)) {
        checks.push(
            hasIntersection(dashboardFilter.channels, scopedSets.channels)
        );
    }

    if (Array.isArray(dashboardFilter.ids)) {
        const idsCheck = checkFilterIdsAgainstGroup(
            dashboardFilter.ids,
            scopedSets
        );

        if (!R.isNil(idsCheck)) {
            checks.push(idsCheck);
        }
    }

    if (!checks.length) {
        return true;
    }

    return checks.every(Boolean);
};

export const filterDimensionsByGroup = (
    dimensions = [],
    groupId,
    tableAdAccountsByGroupId = {}
) =>
    (dimensions || []).filter((dimension) =>
        checkDimensionMatchesGroup(dimension, groupId, tableAdAccountsByGroupId)
    );

export const refineDimensionToLabelByGroup = (
    dimensions = [],
    groupId,
    tableAdAccountsByGroupId = {}
) =>
    refineDimensionToLabel(
        filterDimensionsByGroup(dimensions, groupId, tableAdAccountsByGroupId)
    );

export const isExcludedForLinkedWidget = (widget) => {
    const { type } = widget;
    const excludedTypes = [
        WIDGET_TYPES.FILTER,
        WIDGET_TYPES.ALARMS,
        WIDGET_TYPES.NOTEPAD,
        WIDGET_TYPES.GOAL_CHILD,
    ];
    return listIncludes(excludedTypes, type);
};

/**
 * Returns whether graph labels should be shown by default for a given graph type
 * @param {Object} options - The options object containing graphType
 * @param {string} options.graphType - The type of the graph
 * @returns {boolean} - Whether graph labels should be shown by default
 */
export const getDefaultShowGraphLabel = ({ graphType }) => {
    switch (graphType) {
        case CHART_TYPES.PIE:
        case CHART_TYPES.DONUT:
            return true;
        default:
            return false;
    }
};

export const fieldLocaleMapGenerator = (fieldList) => {
    let map = new Map();

    R.forEach(({ name, locale }) => {
        if (!locale) return;

        map.set(name, locale);
    })(fieldList);

    return map;
};
